import { User, Device } from "../models";
import { APIVersion, AuthenticatedRequest, HTTPMethod, Session, BasicRequest, AuthenticatedFileRequest } from "../requests/request";
import { DeviceData, SubscriptionInfo } from "..";
import { JsonProperty } from "../requests/jsonparser";


export interface PaymentHistoryItem {
    id: number;
    status: string;
    paid_amount: string;
    method: string;
    mode: string;
    plan_name: string;
    period: number;
    created_at: string;
    expire_date: string;
    transition: string;
}

export interface Address {
    first_name: string;
    last_name: string;
    company?: string;
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
    phone?: string;
}

export interface MembershipInfo {
    start_date: Date
    membership_type: string
    membership_info: SubscriptionInfo,
    remainingDays: number
    interval: string
    next_billing: Date
    title: string
    status: string
    current_plan: number
    order: {
        id: number,
        user_id: number,
        transaction_id: number,
        plan_name: string,
        plan_period: number,
        status: string,
        paid_amount: number,
        method: string,
        mode: string,
        from: string,
        created_at: string,
        expire_date: string,
        gateway_id: string,
        updated_at: string,
        is_last_status: boolean,
        gw_version: number
    }
    require_payment_method_change: boolean
}

export class UserMev2 {

    @JsonProperty({ type: 'true' })
    ISHELPERMODEL: string;
    @JsonProperty({ type: 'UserMev2' })
    MODELNAME: string;

    @JsonProperty({ type: 'User', clazz: User })
    user: User

    @JsonProperty({ type: 'any' })
    membership: MembershipInfo
}

export enum RedirectPath {
    MANAGE_SUBSCRIPTION = 'account',
    RECORD_POOL = 'store/newreleases/audio/smart',
    CLOUD_RESCUE = 'account#cloudrescue'
}

export interface UpgradeInfo {
    upgradePrice: number
    renewalDate: Date
    recurringPrice: number
    recurringDisplayPrice: string
}

export interface AdminRole {

}
export interface PaymentInstrument {
    method: 'paypal' | 'authorize',
    email?: string,
    card?: {
        cardNumber: string, cardType: string
    }
}
export class API {

    /**
     * Signs up a new user
     * @returns {Promise<Device>} The Promise contains an APIError if rejected
     * Error Types:
     * 400 InvalidParametersError
     * 403 ResourceAlreadyExists: Email already existing
     * 500 InternalServerError
     */
    public static signUp(email: string, password: string, phone: string, full_name: string, from: string, device: DeviceData): Promise<Device> {
        let apiRequest = new BasicRequest<Device>('/users', HTTPMethod.POST, APIVersion.v1_2, {}, {
            email: email,
            password: password,
            phone: phone,
            name: full_name,
            device: device,
            from
        });

        return apiRequest.send(Device).then((response) => {
            return <Device>response.data;
        });
    }

    /**
     * Fetches the user
     * @returns {Promise<User>} The Promise contains an APIError if rejected
     */
    public static getMe(): Promise<User> {
        let apiRequest = new AuthenticatedRequest<User>('/users/me', HTTPMethod.GET, APIVersion.v1_2);

        return apiRequest.send(User).then((response) => {
            Session.getUserUpdatedHandler()(response.data)
            return <User>response.data;
        });
    }

    /**
     * Fetches the user
     * @returns {Promise<User>} The Promise contains an APIError if rejected
     */
    public static getMev2(): Promise<UserMev2> {
        let apiRequest = new AuthenticatedRequest<UserMev2>('/users/me', HTTPMethod.GET, APIVersion.v2_0);

        return apiRequest.send(UserMev2).then((response) => {
            Session.getUserUpdatedHandler()((<UserMev2>response.data).user)
            return <UserMev2>response.data;
        });
    }

    /**
     * Checks if the user has a Payment Instrument
     * @returns {Promise<User>} The Promise contains an APIError if rejected
     */
    public static getPaymentInstrument(downwardCompatible: boolean = false, platform?: 'create' | 'supreme' | 'stream' | 'sync'): Promise<PaymentInstrument> {
        let apiRequest = new AuthenticatedRequest<{ method: 'paypal' | 'authorize' }>('/users/me/paymentInstrument', HTTPMethod.GET, APIVersion.v3_0, { downwardCompatible, platform });

        return apiRequest.send().then((response) => {
            return response.data;
        });
    }

    /**
     * Checks if the user has a Payment Instrument
     * @returns {Promise<User>} The Promise contains an APIError if rejected
     */
    public static getCreditCard(platform: 'supreme' | 'stream' | 'create' | 'sync'): Promise<{ card: { cardNumber: string, cardType: string } }> {
        let apiRequest = new AuthenticatedRequest('/users/me/credit-card', HTTPMethod.GET, APIVersion.v3_0, { platform });

        return apiRequest.send().then((response) => {
            return response.data;
        });
    }

    /**
     * Checks if HQ is allowed
     * @returns {Promise<User>} The Promise contains an APIError if rejected
     */
    public static setHQ(): Promise<User> {
        let apiRequest = new AuthenticatedRequest<User>('/users/me/hq', HTTPMethod.PUT, APIVersion.v1_2);

        return apiRequest.send(User).then((response) => {
            Session.getUserUpdatedHandler()(response.data)
            return <User>response.data;
        });
    }

    /**
     * Fetches the membership info
     * @returns {Promise<User>} The Promise contains an APIError if rejected
     */
    public static getMembership(requireLibrary: 'music' | 'latin'): Promise<MembershipInfo> {
        let apiRequest = new AuthenticatedRequest<MembershipInfo>('/users/me/membership', HTTPMethod.GET, APIVersion.v1_2, { requireLibrary });
        apiRequest.preventInvalidSubscriptionHandler = true;
        return apiRequest.send().then((response) => {
            return <MembershipInfo>response.data;
        })
    }

    /**
     * Fetches the membership info
     * @returns {Promise<User>} The Promise contains an APIError if rejected
     */
    public static getMembershipv2(requireLibrary: 'music' | 'latin'): Promise<MembershipInfo> {
        let apiRequest = new AuthenticatedRequest<MembershipInfo>('/users/me/membership', HTTPMethod.GET, APIVersion.v2_0, { requireLibrary });
        apiRequest.preventInvalidSubscriptionHandler = true;
        return apiRequest.send().then((response) => {
            return <MembershipInfo>response.data;
        })
    }

    /**
     * Fetches a web redirect url
     * @returns {Promise<User>} The Promise contains an APIError if rejected
     */
    public static getWebRedirect(type: RedirectPath): Promise<{ url: string }> {
        let apiRequest = new AuthenticatedRequest<{ url: string }>('/users/web/redirect', HTTPMethod.POST, APIVersion.v1_2, undefined, {
            path: type
        });

        return apiRequest.send().then((response) => {
            return <{ url: string }>response.data;
        });
    }

    /**
     * Updated the user's password
     * @returns {Promise<User>} The Promise contains an APIError if rejected
     */
    public static changePassword(from: string, to: string): Promise<User> {
        let apiRequest = new AuthenticatedRequest<User>('/users/me/change_password', HTTPMethod.POST, APIVersion.v1_2, undefined, {
            new_password: to,
            old_password: from
        });

        return apiRequest.send(User).then((response) => {
            return <User>response.data;
        });
    }

    /**
     * Retrieves subscription update info
     * @returns {Promise<UpgradeInfo>} The Promise contains an APIError if rejected
     */
    public static upgradeMembership(): Promise<MembershipInfo> {
        let apiRequest = new AuthenticatedRequest<MembershipInfo>('/users/membership/upgrade', HTTPMethod.POST, APIVersion.v1_2);

        return apiRequest.send().then((response) => {
            return <MembershipInfo>response.data;
        });
    }

    public static setTheme(theme: 'black' | 'white'): Promise<User> {
        let apiRequest = new AuthenticatedRequest<User>('/users/me/theme', HTTPMethod.PUT, APIVersion.v1_0, undefined, { theme });

        return apiRequest.send(User).then((response) => {
            Session.getUserUpdatedHandler()(response.data)
            return <User>response.data;
        });
    }

    public static logout(): Promise<void> {
        let apiRequest = new AuthenticatedRequest<void>('/logout', HTTPMethod.POST)
        return apiRequest.send().then((response) => {
            return response.data;
        });
    }

    public static getPayments(platform?: 'supreme' | 'stream' | 'create' | 'sync'): Promise<PaymentHistoryItem[]> {
        let apiRequest = new AuthenticatedRequest<PaymentHistoryItem[]>('/users/me/payments', HTTPMethod.GET, APIVersion.v1_0, { platform });
        return apiRequest.send().then((response) => {
            return response.data;
        });
    }

    public static getInvoiceLink(id: number, download: boolean): string {
        return `${Session.getAPIURL()}/${APIVersion.v1_0}/order/${id}/invoice${download ? '?download=true' : ''}`
    }

    public static getAddress(): Promise<Address> {
        let apiRequest = new AuthenticatedRequest<Address>('/users/me/address', HTTPMethod.GET, APIVersion.v1_0);

        return apiRequest.send().then((response) => {
            return <Address>response.data;
        });
    }

    public static setAddress(address: Address): Promise<Address> {
        let apiRequest = new AuthenticatedRequest<Address>('/users/me/address', HTTPMethod.POST, APIVersion.v1_0, undefined, address);

        return apiRequest.send().then((response) => {
            return <Address>response.data;
        });
    }

    /**
     * 
     * If code 403 is returned, to user name is already takes
     */
    public static updateMe(update: { username?: string, tshirt_size?: string, affiliation?: string, email?: string, dob?: Date, dj_type?: string }): Promise<User> {
        let apiRequest = new AuthenticatedRequest<User>('/users/me', HTTPMethod.PUT, APIVersion.v1_0, undefined, update);

        return apiRequest.send(User).then((response) => {
            Session.getUserUpdatedHandler()(response.data)
            return <User>response.data;
        });
    }

    public static updateUser(id: number, user: User): Promise<User> {
        let apiRequest = new AuthenticatedRequest<User>('/admin/user/' + id, HTTPMethod.PUT, APIVersion.v1_0, undefined, user);

        return apiRequest.send().then((response) => {
            Session.getUserUpdatedHandler()(response.data)
            return <User>response.data;
        });
    }

    public static loginWithUser(id: number): Promise<{ token: string }> {
        let apiRequest = new AuthenticatedRequest<User>('/admin/user/' + id + '/tempLogin', HTTPMethod.POST, APIVersion.v1_0);
        return apiRequest.send().then((response) => {
            return <{ token: string }>response.data;
        });
    }

    public static userById(id: number, withAdminRoleId: boolean): Promise<User> {
        let apiRequest = new AuthenticatedRequest<User>('/admin/user/' + id, HTTPMethod.GET, APIVersion.v1_0, { withAdminRoleId });

        return apiRequest.send().then((response) => {
            Session.getUserUpdatedHandler()(response.data)
            return <User>response.data;
        });
    }

    public static getUsersMembership(platform: 'supreme' | 'stream' | 'create' | 'sync', userId: string) {
        let apiRequest = new AuthenticatedRequest<MembershipInfo>('/admin/user/' + userId + '/membership', HTTPMethod.GET, APIVersion.v1_0, { platform });
        apiRequest.preventInvalidSubscriptionHandler = true;
        return apiRequest.send().then((response) => {
            return <MembershipInfo>response.data;
        })
    }

    /**
     * Updated the user's password
     * @returns {Promise<User>} The Promise contains an APIError if rejected
     */
    public static adminChangePassword(userId: string, password: string): Promise<User> {
        let apiRequest = new AuthenticatedRequest<User>('/admin/user/' + userId + '/password', HTTPMethod.PUT, APIVersion.v1_0, undefined, {
            password,
        });

        return apiRequest.send(User).then((response) => {
            return <User>response.data;
        });
    }

    /*public static adminPreviewPlanChange(userId: string, planId: number, prorata: boolean, coupon?: string): Promise<SubscriptionPackageChange> {
        let apiRequest = new AuthenticatedRequest<SubscriptionPackageChange>('/admin/user/' + userId + '/changeSubscription', HTTPMethod.PUT, APIVersion.v1_0, undefined, {
            preview: true,
            coupon,
            prorata,
            planId
        });
        return apiRequest.send().then((response) => {
            return response.data;
        });
    }

    public static adminChangePlan(userId: string, planId: number, prorata: boolean, coupon?: string): Promise<SubscriptionPackageChange> {
        let apiRequest = new AuthenticatedRequest<SubscriptionPackageChange>('/admin/user/' + userId + '/changeSubscription/' + planId, HTTPMethod.PUT, APIVersion.v1_0, undefined, {
            preview: false,
            coupon,
            prorata
        });
        return apiRequest.send().then((response) => {
            return response.data;
        });
    }*/

    public static getAdminRoles() {
        let apiRequest = new AuthenticatedRequest<AdminRole[]>('/admin/user/admin/roles', HTTPMethod.GET, APIVersion.v1_0);
        return apiRequest.send().then((response) => {
            return <AdminRole[]>response.data;
        })
    }

    public static resetDownloads(userId: number, ids: number[]) {
        let apiRequest = new AuthenticatedRequest<AdminRole[]>('/admin/user/' + userId + '/downloads/resetMultiId', HTTPMethod.POST, APIVersion.v1_0, undefined, { ids });
        return apiRequest.send().then((response) => {
            return response.data;
        })
    }

    public static deleteProfilePicture(): Promise<User> {
        let apiRequest = new AuthenticatedRequest<User>('/users/me/picture', HTTPMethod.DELETE, APIVersion.v1_0);
        return apiRequest.send(User).then((response) => {
            Session.getUserUpdatedHandler()(response.data)
            return response.data;
        });
    }

    public static uploadProfilePicture(image: any): Promise<User> {
        let apiRequest = new AuthenticatedFileRequest<User>('/users/me/picture', HTTPMethod.POST, APIVersion.v1_0, undefined, {
            image
        });
        return apiRequest.send(User).then((response) => {
            Session.getUserUpdatedHandler()(response.data)
            return response.data;
        });
    }
}



// WEBPACK FOOTER //
// ./src/api/src/endpoints/user.ts